import * as React from 'react';

import type Moment from 'moment';

import {ListRow} from '../list-row/list-row';

import {getActionOptionsForEntities} from './get-action-options-for-entities';
import {TodoItem} from './todo-item';

type Props = {
    style?: any;
    todo: any;
    onComplete?: (todo: any) => void;
    onEdit?: (todo: any) => void;
    onDelete?: (todo: any) => void;
    onDelayConfirm?: (todo: any, delayId: string) => void;
    onSkipConfirm?: (todo: any) => void;
    onUncompleteConfirm?: (todo: any) => void;
    onClearError?: (todo: any) => void;
    // @ts-expect-error
    onTimeChange?: (todo: any, date: Moment) => void;
    hideActionMenu?: boolean;
    hasActions?: boolean;
    hasBorders?: boolean;
    onStartEmailToEntity?: (entityId: string) => any;
    onStartCallToEntity?: (arg1: {
        entityId: string;
        phoneNumber: {
            E164: string;
            numberFormatted: string;
        };
        anchorElementId: string;
    }) => void;
    fetchRelatedLead?: (leadId: string) => void;
    leadModel?: any;
};

type State = {
    confirmState: string | null | undefined;
};

export class TodoListItem extends React.Component<Props, State> {
    static defaultProps = {
        hasActions: true,
    };

    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            confirmState: null,
        };
    }

    render() {
        const {todo, style} = this.props;
        const options = this.getOptionsForTodo();

        return (
            <ListRow
                errorMessage={todo.errorMessage}
                onClearError={this.handleClearError}
                to={
                    todo.htmlUrlPath
                        ? {pathname: todo.htmlUrlPath, state: {modal: true}}
                        : undefined
                }
                hasBorders={this.props.hasBorders}
                hideActionMenu={this.props.hideActionMenu}
                onOpenActionMenu={this.handleOpenActionMenu}
                options={options}
                style={style}
                anchorElementId={this.getAnchorElementId()}
            >
                <TodoItem
                    todo={todo}
                    hasActions={this.props.hasActions}
                    isUncompleting={this.state.confirmState === 'uncomplete'}
                    isSkipping={this.state.confirmState === 'skip'}
                    isDelaying={this.state.confirmState === 'delay'}
                    isDeleting={this.state.confirmState === 'delete'}
                    onComplete={this.handleComplete}
                    onUncomplete={this.handleUncomplete}
                    onUncompleteCancel={this.handleUncompleteCancel}
                    onUncompleteConfirm={this.handleUncompleteConfirm}
                    onDelayConfirm={this.handleDelayConfirm}
                    onDelayCancel={this.handleDelayCancel}
                    onSkipConfirm={this.handleSkipConfirm}
                    onSkipCancel={this.handleSkipCancel}
                    onDeleteConfirm={this.handleDeleteConfirm}
                    onDeleteCancel={this.handleDeleteCancel}
                    onTimeChange={this.handleTimeChange}
                />
            </ListRow>
        );
    }

    handleDelay = () => {
        this.setState({confirmState: 'delay'});
    };

    handleDelayCancel = (e: React.SyntheticEvent<any>) => {
        e.stopPropagation();
        this.setState({confirmState: null});
    };

    handleSkip = () => {
        this.setState({confirmState: 'skip'});
    };

    handleSkipCancel = (e: React.SyntheticEvent<any>) => {
        e.stopPropagation();
        this.setState({confirmState: null});
    };

    handleUncomplete = () => {
        this.setState((prevState) => ({
            confirmState: prevState.confirmState !== 'uncomplete' ? 'uncomplete' : undefined,
        }));
    };

    handleUncompleteCancel = () => {
        this.setState({confirmState: null});
    };

    handleComplete = () => {
        if (this.props.onComplete) {
            this.props.onComplete(this.props.todo);
        }
    };

    handleDelayConfirm = (delayId: string) => {
        this.setState({confirmState: null});
        if (this.props.onDelayConfirm) {
            this.props.onDelayConfirm(this.props.todo, delayId);
        }
    };

    handleSkipConfirm = (e: React.SyntheticEvent<any>) => {
        e.stopPropagation();
        this.setState({confirmState: null});
        if (this.props.onSkipConfirm) {
            this.props.onSkipConfirm(this.props.todo);
        }
    };

    handleUncompleteConfirm = (e: React.SyntheticEvent<any>) => {
        e.stopPropagation();
        this.setState({confirmState: null});
        if (this.props.onUncompleteConfirm) {
            this.props.onUncompleteConfirm(this.props.todo);
        }
    };

    handleClearError = () => {
        if (this.props.onClearError) {
            this.props.onClearError(this.props.todo);
        }
    };

    handleEdit = () => {
        if (this.props.todo.type === 'tasks' && this.props.onEdit) {
            this.props.onEdit(this.props.todo);
        }
    };

    handleDelete = () => {
        this.setState({confirmState: 'delete'});
    };

    handleDeleteCancel = (e: React.SyntheticEvent<any>) => {
        e.stopPropagation();
        this.setState({confirmState: null});
    };

    handleDeleteConfirm = (e: React.SyntheticEvent<any>) => {
        e.stopPropagation();
        this.setState({confirmState: null});
        if (this.props.todo.type === 'tasks' && this.props.onDelete) {
            this.props.onDelete(this.props.todo);
        }
    };

    handleOpenActionMenu = () => {
        if (this.props.fetchRelatedLead && this.props.todo.relatedEntity.type === 'leads') {
            this.props.fetchRelatedLead(this.props.todo.relatedEntity.id);
        }
    };

    // @ts-expect-error
    handleTimeChange = (date: Moment) => {
        if (this.props.onTimeChange) {
            this.props.onTimeChange(this.props.todo, date);
        }
    };

    handleStartCallToEntity = (entity: any) => {
        if (this.props.onStartCallToEntity) {
            const anchorElementId = this.getAnchorElementId();
            this.props.onStartCallToEntity({
                entityId: entity.entityId,
                phoneNumber: {
                    E164: entity.phoneNumber.E164,
                    numberFormatted: entity.phoneNumber.numberFormatted,
                },
                anchorElementId: anchorElementId,
            });
        }
    };

    getOptionsForTodo = () => {
        const {todo} = this.props;
        const options = [];

        if (todo.type === 'tasks') {
            options.push({
                label: 'Edit',
                handleSelect: () => this.handleEdit(),
            });
            options.push({
                label: 'Delete',
                handleSelect: () => this.handleDelete(),
            });
        }

        if (todo.isDelayable) {
            options.push({
                label: 'Delay',
                handleSelect: () => this.handleDelay(),
            });
        }

        if (todo.isSkippable) {
            options.push({
                label: 'Skip',
                handleSelect: () => this.handleSkip(),
            });
        }

        // If we don't have a related entity, bail now
        if (!todo.relatedEntity) {
            return options;
        }

        // If the related entity is a lead, we need to get its related entities
        if (todo.relatedEntity.type === 'leads') {
            // If it hasn't been fetched yet, display a loading option
            if (!this.props.leadModel) {
                options.unshift({key: 'loading', label: 'Loading…', isDisabled: true});
            } else {
                // Otherwise, get related entities, and tack on those options
                const relatedEntities = this.props.leadModel.accounts.concat(
                    this.props.leadModel.contacts
                );

                if (this.props.onStartEmailToEntity && this.props.onStartCallToEntity) {
                    getActionOptionsForEntities(relatedEntities, {
                        email: this.props.onStartEmailToEntity,
                        call: this.handleStartCallToEntity,
                    }).forEach((opt) => options.unshift(opt));
                }
            }
        } else if (this.props.onStartEmailToEntity && this.props.onStartCallToEntity) {
            getActionOptionsForEntities([todo.relatedEntity], {
                email: this.props.onStartEmailToEntity,
                call: this.handleStartCallToEntity,
            }).forEach((opt) => options.unshift(opt));
        }

        return options;
    };

    getAnchorElementId = () => {
        return this.props.todo.id;
    };
}
